/**
 * The server can only skip browser session discovery when the incoming page
 * request proves that none of the credentials accepted by api-go exists.
 * "unknown" deliberately includes valid, expired, malformed, and legacy
 * credentials: all of those must keep the normal refresh/session path.
 */
export type AuthBootstrapStatus = "anonymous" | "unknown";

// Kept in lockstep with apps/api-go/internal/usecase/auth/auth.go:
// sessionCookieBase, refreshCookieBase, their production __Secure- variants,
// and the read-only legacySessionCookieName fallback.
export const AUTH_CREDENTIAL_COOKIE_NAMES = [
  "__Secure-better-auth.session_token",
  "better-auth.session_token",
  "__Secure-better-auth.refresh_token",
  "better-auth.refresh_token",
  "noiman_session",
] as const;

const AUTH_CREDENTIAL_COOKIE_NAME_SET = new Set<string>(
  AUTH_CREDENTIAL_COOKIE_NAMES,
);

function hasAcceptedAuthCookie(cookieHeader: string): boolean {
  for (const part of cookieHeader.split(";")) {
    const trimmed = part.trim();
    if (!trimmed) continue;
    const equals = trimmed.indexOf("=");
    const name = (equals === -1 ? trimmed : trimmed.slice(0, equals)).trim();
    if (AUTH_CREDENTIAL_COOKIE_NAME_SET.has(name)) return true;
  }
  return false;
}

export function getRequestAuthBootstrapStatus(
  request: Pick<Request, "headers">,
): AuthBootstrapStatus {
  // Conservatively retain the normal session lookup for callers which carry
  // an explicit credential outside the browser cookie jar.
  if (request.headers.get("authorization")?.trim()) return "unknown";

  const cookieHeader = request.headers.get("cookie");
  if (cookieHeader && hasAcceptedAuthCookie(cookieHeader)) return "unknown";

  return "anonymous";
}

// Kept in lockstep with api-go's refreshCookieName() (both variants).
const REFRESH_COOKIE_NAMES = new Set<string>([
  "__Secure-better-auth.refresh_token",
  "better-auth.refresh_token",
]);

export function hasRefreshCookie(cookieHeader: string | null): boolean {
  if (!cookieHeader) return false;
  for (const part of cookieHeader.split(";")) {
    const trimmed = part.trim();
    const equals = trimmed.indexOf("=");
    if (equals === -1) continue;
    const name = trimmed.slice(0, equals).trim();
    if (REFRESH_COOKIE_NAMES.has(name) && trimmed.slice(equals + 1) !== "") {
      return true;
    }
  }
  return false;
}

/**
 * The refresh cookie is HttpOnly, so browser code cannot tell whether a 401
 * is recoverable. The Worker proxy can: it sees the Cookie header the browser
 * sent and stamps this header on a proxied 401 when no refresh cookie was
 * attached. api-go answers such a refresh with a bare 401 and no Set-Cookie,
 * so the browser can skip that round-trip without losing anything.
 */
export const REFRESH_COOKIE_STATE_HEADER = "X-Yumesaku-Refresh-Cookie";
export const REFRESH_COOKIE_ABSENT = "absent";
